/**
 * Player profiles (game state + public summary) with optimistic concurrency, pending grants and legacy save import.
 * Replaces the original VK storage / SharedObject save chain (`save storage` -> `check storage`, FWP.as:14340-14647)
 * and the "one window" sid check (`set sid`/`prove sid`) with `baseRevision` conflicts plus a `profile.changed` event.
 *
 * Ownership of the game state depends on the trust level (BackendConfig.trustLevel):
 *  - L0/L1: the client owns it. It creates a new profile itself, runs the session entry and saves whole states
 *    (`profile.save`, validated at L1). `profile.load` returns the stored state as it is.
 *  - L2: the server owns it (design D1). `profile.load` creates the state of a new account (SERVER op
 *    profile.create) and runs the session entry (SERVER op session.enter) on every load; the game changes the state
 *    only through `player.command` (./commands.ts). `profile.save` / `profile.importLegacy` are refused, except the
 *    import of a local or legacy save into a FRESH profile when config.allowLegacyImport (isFreshProfile).
 * Every level records the device time zone the client reports on load (rules-runtime decideTimeZone).
 *
 * The profile document (ProfileRecord) also holds the server state, the command journal, the time zone and the
 * `fresh` flag; every write keeps them (one document write), and every read-modify-write runs under the per-player
 * lock `profileLockKey(playerId)` that commands, saves, loads and name changes share.
 */
import type { JsonValue } from '../../../shared/json';
import { randomId } from '../../../shared/random';
import { inspectLegacyStorage, readLegacyScalars } from '../../../shared/legacy/storage-v45';
import { BackendError } from '../../../shared/protocol/errors';
import type { EventMap } from '../../../shared/protocol/messages';
import type { ListPage, PayloadOf, ResultOf } from '../../../shared/protocol/messages';
import { LIST_LIMITS } from '../../../shared/protocol/validators';
import {
  type Grant,
  type LegacyImport,
  type PlayerCard,
  type PlayerId,
  type PlayerProfile,
  type ProfileSummary,
  type ProfileVolatile,
  type PublicProfile,
  type TeamSnapshot,
  type TeamSnapshotInput,
  emptyProfileSummary,
} from '../../../shared/protocol/model';
import { SERVER_RULE_OPS, decodeState, isRuleError, type StoredGameState } from '../../../game/rules/index';
import { emptyGameState } from '../../../game/rules/ops/progress';
import type { SessionEnterResult } from '../../../game/rules/ops/progress-profile';
import type { ConnectionContext } from '../hub';
import type { ProfileProjector } from '../projector';
import { cursorInt, decodeCursor, encodeCursor, keysetPage, pageLimit } from '../pagination';
import { type ServerState, decideTimeZone, runRulesOp, serverRuleContext } from '../rules-runtime';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import type { ProfileValidator } from '../validation';
import type { AuthService } from './auth';
import type { ClanDirectory } from './clan-directory';
import { type AccountRecord, type ProfileRecord, type ServiceDeps, C } from './deps';
import { type EconomyChange, type EconomyListener, type EconomySource, anchorOf, gapOf } from './economy';
import { currenciesOf } from './resource-ledger';

export type ProfileSavedListener = (rec: ProfileRecord, account: AccountRecord) => void;

/** An operator's change of a canonical state (ProfileService.adminSetState `extra`). */
export interface AdminStateChange {
  /** The stored state before the change. */
  before: StoredGameState;
  /** The state being stored. */
  after: StoredGameState;
  /** The revision the change gets. */
  revision: number;
  /** Server time of the change, epoch ms. */
  at: number;
}

/** Key of the per-player lock of the profile document (commands, saves, loads, imports, name changes). */
export function profileLockKey(playerId: PlayerId): string {
  return `profile:${playerId}`;
}

/**
 * A profile that an import may still replace: it has no game state yet, or its state is the server-created initial
 * state that only session entries changed since (ProfileRecord.fresh: set by profile.create, cleared by every
 * successful command, save and import). Records of older backends have no flag: they are fresh only without a state.
 */
export function isFreshProfile(rec: ProfileRecord): boolean {
  return rec.profile.gameState === null || rec.fresh === true;
}

/** serverState key of today's daily present (written by the session entry, consumed by the present.claim command). */
export const PRESENT_KEY = 'present';

/** serverState[PRESENT_KEY]: the daily present the session entry found due. */
export interface PendingPresent {
  [field: string]: number;
  /** Login streak the present is for (FWP.get_present = tween). */
  tween: number;
  /** The player's local day (RuleContext.calendar(now).dayIndex) of the entry. */
  day: number;
  /** Server time of the entry, unix seconds. */
  at: number;
}

/** The pending present of a server state, if any (malformed entries count as none). */
export function pendingPresent(serverState: ServerState | undefined): PendingPresent | null {
  const v = serverState?.[PRESENT_KEY];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const p = v as Record<string, unknown>;
  if (typeof p.tween !== 'number' || typeof p.day !== 'number' || typeof p.at !== 'number') return null;
  return { tween: p.tween, day: p.day, at: p.at };
}

/**
 * Pending grants (C.grants) of the client-owned profiles (trust levels L0 / L1: profile.save consumes them). Deprecated:
 * no server path creates grants any more (operator grants change the canonical state directly at L2, design D7), so
 * the collection holds only grants stored by earlier versions; `create` stays for them and the tests. The grants are
 * read from an in-memory index by player (built from the collection once per process), never by listing the
 * collection on every profile.load.
 */
export class GrantService {
  private byPlayer: Map<PlayerId, Map<string, Grant>> | null = null;
  private loading: Promise<Map<PlayerId, Map<string, Grant>>> | null = null;

  constructor(private readonly d: ServiceDeps) {}

  private index(): Promise<Map<PlayerId, Map<string, Grant>>> {
    if (this.byPlayer) return Promise.resolve(this.byPlayer);
    this.loading ??= (async () => {
      const all = await this.d.storage.list<Grant>(C.grants);
      const index = new Map<PlayerId, Map<string, Grant>>();
      for (const g of all) if (g && typeof g.grantId === 'string' && typeof g.playerId === 'string') indexGrant(index, g);
      this.byPlayer = index;
      return index;
    })().finally(() => (this.loading = null));
    return this.loading;
  }

  /** Deprecated (see the class comment): stores a grant for the player and tells its connections (grant.received). */
  async create(grant: Omit<Grant, 'grantId' | 'createdAt'>): Promise<Grant> {
    const g: Grant = { ...grant, grantId: randomId(this.d.rng, 'g_', 10), createdAt: this.d.clock.now() };
    await this.d.storage.batch([putOp(C.grants, g.grantId, g)]);
    indexGrant(await this.index(), g);
    this.d.hub.send([g.playerId], 'grant.received', { grant: g } satisfies EventMap['grant.received']);
    return g;
  }

  async pending(playerId: PlayerId): Promise<Grant[]> {
    const mine = (await this.index()).get(playerId);
    return [...(mine?.values() ?? [])].sort((a, b) => a.createdAt - b.createdAt || (a.grantId < b.grantId ? -1 : a.grantId > b.grantId ? 1 : 0));
  }

  /**
   * One page of `pending()` (oldest first, then grantId): `limit` grants after `cursor` (the `next` of the previous
   * page). Cursor `gr.<createdAt>.<grantId>`; a malformed one is BAD_REQUEST 'bad_cursor'.
   */
  async pendingPage(playerId: PlayerId, opts: { limit?: number; cursor?: string } = {}): Promise<ListPage<Grant>> {
    const limit = pageLimit(opts.limit, LIST_LIMITS['profile.listGrants'].default, LIST_LIMITS['profile.listGrants'].max);
    let after: ((g: Grant) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [at, id] = decodeCursor(opts.cursor, 'gr', 2);
      const createdAt = cursorInt(at);
      after = (g) => g.createdAt > createdAt || (g.createdAt === createdAt && g.grantId > id);
    }
    return keysetPage(await this.pending(playerId), limit, after, (g) => encodeCursor('gr', [g.createdAt, g.grantId]));
  }

  /** Checks that all ids are pending grants of the player (does not delete). */
  async assertConsumable(playerId: PlayerId, ids: readonly string[]): Promise<void> {
    for (const id of new Set(ids)) {
      const g = await this.d.storage.get<Grant>(C.grants, id);
      if (!g || g.playerId !== playerId) throw new BackendError('VALIDATION_FAILED', `Grant ${id} is not pending for this player`, { reason: 'grant_not_pending', grantId: id });
    }
  }

  async consume(ids: readonly string[]): Promise<void> {
    await this.d.storage.batch(this.consumeOps(ids));
    await this.consumed(ids);
  }

  /** Account deletion: the deletes of every pending grant of the player. */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    return (await this.pending(playerId)).map((g) => deleteOp(C.grants, g.grantId));
  }

  /** The deletes that consume grants, for a batch with the profile write that credits them (then call `consumed`). */
  consumeOps(ids: readonly string[]): StorageBatchOp[] {
    return [...new Set(ids)].map((id) => deleteOp(C.grants, id));
  }

  /** After the batch that deleted them: the grants leave the index. */
  async consumed(ids: readonly string[]): Promise<void> {
    if (ids.length === 0) return;
    const index = await this.index();
    for (const id of ids) {
      for (const [pid, mine] of index) {
        if (!mine.delete(id)) continue;
        if (mine.size === 0) index.delete(pid);
        break;
      }
    }
  }

  /** An operator restored a damaged grant (admin server.repairDocument): the index, built without it, takes it in. */
  async repaired(g: Grant): Promise<void> {
    if (g && typeof g.grantId === 'string' && typeof g.playerId === 'string') indexGrant(await this.index(), g);
  }

  /** Account deletion: after its batch, the player's grants leave the index. */
  async forget(playerId: PlayerId): Promise<void> {
    (await this.index()).delete(playerId);
  }
}

function indexGrant(index: Map<PlayerId, Map<string, Grant>>, g: Grant): void {
  let mine = index.get(g.playerId);
  if (!mine) index.set(g.playerId, (mine = new Map()));
  mine.set(g.grantId, g);
}

export class ProfileService {
  private readonly listeners = new Set<ProfileSavedListener>();
  private readonly economyListeners = new Set<EconomyListener>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly grants: GrantService,
    private readonly projector: ProfileProjector,
    private readonly validator: ProfileValidator | null,
    private readonly levelOf: (exp: number) => number,
    /** The clan of a public profile (services/clan-directory.ts). */
    private readonly clans: ClanDirectory,
  ) {}

  onSaved(l: ProfileSavedListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /**
   * Calls `l` with every stored change of a canonical state that changed a balance or found a gap (./economy.ts: the
   * income attribution the anti-cheat reads). A failing listener is logged; the write stays.
   */
  onEconomy(l: EconomyListener): () => void {
    this.economyListeners.add(l);
    return () => this.economyListeners.delete(l);
  }

  /**
   * Income attribution of a write about to be stored (./economy.ts): the balances before and after, the gap between
   * the stored balance and the anchor, and the new anchor set on `rec` (written with it). Returns the change to announce
   * after the write (null: no balance changed and no gap).
   */
  private attribute(rec: ProfileRecord, next: JsonValue | null, revision: number, nowMs: number, source: EconomySource): EconomyChange | null {
    const before = currenciesOf(rec.profile.gameState);
    const after = currenciesOf(next);
    const gap = gapOf(rec.econ, before, rec.profile.revision);
    const anchor = anchorOf(after, revision, nowMs);
    if (anchor) rec.econ = anchor;
    else delete rec.econ;
    const changed = (before?.gold ?? 0) !== (after?.gold ?? 0) || (before?.diam ?? 0) !== (after?.diam ?? 0);
    if (!changed && !gap) return null;
    return { playerId: rec.profile.id, at: nowMs, revision, before, after, source: { ...source }, gap };
  }

  private announceEconomy(change: EconomyChange | null): void {
    if (!change) return;
    for (const l of this.economyListeners) {
      try {
        l(change);
      } catch (err) {
        this.d.logger.error('economy listener failed', { playerId: change.playerId, err });
      }
    }
  }

  async getRecord(playerId: PlayerId): Promise<ProfileRecord | null> {
    return this.d.storage.get<ProfileRecord>(C.profiles, playerId);
  }

  async listRecords(): Promise<ProfileRecord[]> {
    return this.d.storage.list<ProfileRecord>(C.profiles);
  }

  private get authoritative(): boolean {
    return this.d.config.trustLevel === 'L2';
  }

  private blank(a: AccountRecord): ProfileRecord {
    return {
      profile: {
        id: a.playerId,
        displayName: a.displayName,
        createdAt: a.createdAt,
        updatedAt: a.createdAt,
        revision: 0,
        schemaVersion: 1,
        gameState: null,
        summary: emptyProfileSummary(),
        legacyImport: null,
      },
      team: null,
    };
  }

  /**
   * Runs `fn` with the player's account and profile document (a private copy; a new account gets a blank one) under
   * the per-player lock. `fn` persists what it changes (storeRecord / commitCanonical).
   */
  withRecord<T>(playerId: PlayerId, fn: (rec: ProfileRecord, account: AccountRecord) => Promise<T>): Promise<T> {
    return this.d.mutex.run(profileLockKey(playerId), async () => {
      const a = await this.auth.requireAccount(playerId);
      const rec = (await this.getRecord(playerId)) ?? this.blank(a);
      return fn(rec, a);
    });
  }

  /**
   * Writes the document; `notify` = the public projection changed (leaderboards, listeners). `extra`: other documents
   * of the same operation, written in ONE atomic storage batch with the profile (a battle's replay, a zone capture).
   */
  async storeRecord(rec: ProfileRecord, a: AccountRecord, notify: boolean, extra: readonly StorageBatchOp[] = []): Promise<void> {
    if (extra.length > 0) await this.d.storage.batch([putOp(C.profiles, a.playerId, rec), ...extra]);
    else await this.d.storage.put(C.profiles, a.playerId, rec);
    if (notify) for (const l of this.listeners) l(rec, a);
  }

  /**
   * A new canonical game state (server-authoritative change: a command, a session entry, a profile creation): the next
   * revision, and the summary and team snapshot projected from the state alone (it is the server's own state, so no
   * client values are merged in). Writes the document, notifies the listeners and, unless `event` is false, sends
   * profile.changed to the player's connections except `event` (the one that caused the change). `extra`: other
   * documents of the same operation, stored atomically with the profile (storeRecord). `source`: where a change of the
   * gold and diamonds comes from (./economy.ts; default: an operator's change when `extra` holds its resource ledger
   * entry, else 'unknown'), announced to the economy listeners after the write.
   */
  async commitCanonical(
    rec: ProfileRecord,
    a: AccountRecord,
    gameState: StoredGameState,
    nowMs: number,
    event: ConnectionContext | null | false,
    extra: readonly StorageBatchOp[] = [],
    source?: EconomySource,
  ): Promise<PlayerProfile> {
    const summary = this.projector.summary(gameState as unknown as JsonValue, undefined, null);
    const ledgerOp = extra.find((o) => o.op === 'put' && o.collection === C.resourceLedger);
    const change = this.attribute(rec, gameState as unknown as JsonValue, rec.profile.revision + 1, nowMs, source ?? (ledgerOp ? { kind: 'admin', ref: ledgerOp.id } : { kind: 'unknown' }));
    rec.profile = { ...rec.profile, displayName: a.displayName, revision: rec.profile.revision + 1, updatedAt: nowMs, gameState: gameState as unknown as JsonValue, summary };
    rec.team = this.buildTeam(a, summary, this.projector.team(gameState as unknown as JsonValue, undefined));
    await this.storeRecord(rec, a, true, extra);
    this.announceEconomy(change);
    if (event !== false) this.d.hub.send([a.playerId], 'profile.changed', { revision: rec.profile.revision, updatedAt: nowMs }, event ?? undefined);
    return rec.profile;
  }

  async load(playerId: PlayerId, p: PayloadOf<'profile.load'> = {}): Promise<ResultOf<'profile.load'>> {
    return this.withRecord(playerId, async (rec, a) => {
      const nowMs = this.d.clock.now();
      const tz = decideTimeZone(rec.tz, p.tzOffsetMin, nowMs);
      if (tz.changed) rec.tz = tz.stored;
      let created = rec.profile.revision === 0;
      let volatile: ProfileVolatile | undefined;
      let committed = false;
      if (this.authoritative) {
        const entry = this.enterSession(rec, { nowMs, tzOffsetMin: tz.offsetMin, dev: p.dev === true && this.d.config.devProfiles });
        if (entry) {
          created = entry.created;
          volatile = entry.volatile;
          if (entry.gameState) {
            // No profile.changed: every other tab reloads on that event, and its load would enter again (ping-pong).
            await this.commitCanonical(rec, a, entry.gameState, nowMs, false, [], entry.created ? { kind: 'start', op: 'profile.create', dev: entry.dev } : { kind: 'session', op: 'session.enter' });
            committed = true;
          }
        }
      }
      if (!committed && tz.changed) await this.storeRecord(rec, a, false);
      rec.profile.displayName = a.displayName;
      const pendingGrants = (await this.grants.pending(playerId)).length;
      const out: ResultOf<'profile.load'> = { profile: rec.profile, created, serverTime: nowMs, pendingGrants };
      if (volatile) out.volatile = volatile;
      return out;
    });
  }

  /**
   * L2 session entry of profile.load, on `rec` in place (serverState, fresh): creates the state of a new account
   * (profile.create, then session.enter { created: true } as the game's first start did), or enters the stored state.
   * Returns null when there is nothing to enter: a pending legacy import the client decodes first (only while imports
   * are allowed), or a stored state the rules cannot read (older saves of other shapes are returned as stored).
   * `gameState` is the new state, or null when the entry changed nothing (same second, same day).
   */
  private enterSession(rec: ProfileRecord, env: { nowMs: number; tzOffsetMin: number; dev: boolean }): { created: boolean; dev: boolean; gameState: StoredGameState | null; volatile: ProfileVolatile } | null {
    const serverState: ServerState = { ...(rec.serverState ?? {}) };
    const ctx = serverRuleContext({ nowMs: env.nowMs, tzOffsetMin: env.tzOffsetMin, random: this.d.ruleRng, serverState });
    let state = rec.profile.gameState;
    let created = false;
    let devState = false;
    // The state auth.signUp created and nobody entered yet: this load is the game's first start with it.
    const signUpState = state !== null && rec.entryPending === rec.profile.revision;
    if (state === null || (signUpState && env.dev)) {
      if (state === null && rec.profile.legacyImport && this.d.config.allowLegacyImport) return null;
      state = runRulesOp('profile.create', emptyGameState(), { dev: env.dev }, ctx, SERVER_RULE_OPS).gameState as unknown as JsonValue;
      created = true;
      devState = env.dev;
    } else if (signUpState) created = true;
    delete rec.entryPending;
    let entered;
    try {
      entered = runRulesOp('session.enter', state, created ? { created: true } : {}, ctx, SERVER_RULE_OPS);
    } catch (err) {
      if (!created && isRuleError(err)) {
        this.d.logger.warn('session entry skipped: the stored game state is not readable by the rules', { playerId: rec.profile.id, reason: err.reason });
        return null;
      }
      throw err;
    }
    const r = entered.result as unknown as SessionEnterResult;
    if (r.newDay) {
      if (r.presentAvailable) serverState[PRESENT_KEY] = { tween: r.present, day: ctx.calendar(ctx.now).dayIndex, at: ctx.now };
      else delete serverState[PRESENT_KEY];
    }
    const present = pendingPresent(serverState);
    const changed = created || JSON.stringify(entered.gameState) !== JSON.stringify(rec.profile.gameState) || JSON.stringify(serverState) !== JSON.stringify(rec.serverState ?? {});
    rec.serverState = serverState;
    if (created) rec.fresh = true;
    return {
      created,
      dev: devState,
      gameState: changed ? entered.gameState : null,
      volatile: { papaHere: r.papaHere, newDay: r.newDay, presentAvailable: present !== null, present: present?.tween ?? 0, tzOffsetMin: r.tzOffsetMin },
    };
  }

  /**
   * auth.signUp: the profile document of a NEW account with its initial game state (profile.create), not stored: the
   * sign-up writes it in one batch with the account and its session, then calls `announce`. The session entry is the
   * first profile.load's (ProfileRecord.entryPending).
   */
  initialRecord(a: AccountRecord): ProfileRecord {
    const nowMs = this.d.clock.now();
    const rec = this.blank(a);
    const ctx = serverRuleContext({ nowMs, tzOffsetMin: 0, random: this.d.ruleRng, serverState: {} });
    const gameState = runRulesOp('profile.create', emptyGameState(), { dev: false }, ctx, SERVER_RULE_OPS).gameState as unknown as JsonValue;
    const summary = this.projector.summary(gameState, undefined, null);
    rec.profile = { ...rec.profile, displayName: a.displayName, revision: 1, updatedAt: nowMs, gameState, summary };
    rec.team = this.buildTeam(a, summary, this.projector.team(gameState, undefined));
    rec.serverState = {};
    rec.fresh = true;
    rec.entryPending = rec.profile.revision;
    const anchor = anchorOf(currenciesOf(gameState), rec.profile.revision, nowMs);
    if (anchor) rec.econ = anchor;
    return rec;
  }

  /** Tells the listeners (leaderboards) about a profile document stored by another service's batch. */
  announce(rec: ProfileRecord, a: AccountRecord): void {
    for (const l of this.listeners) l(rec, a);
  }

  /**
   * The player card (auth.signUp / auth.signIn / auth.session / profile.me): the account's nickname (display name for
   * guest and username accounts) and the level and experience of the canonical game state.
   */
  async playerCard(playerId: PlayerId): Promise<PlayerCard> {
    const a = await this.auth.requireAccount(playerId);
    const rec = await this.getRecord(playerId);
    const summary = rec && rec.profile.revision > 0 ? rec.profile.summary : null;
    const card: PlayerCard = {
      playerId,
      nickname: a.nickname ?? a.displayName,
      level: summary?.level ?? 1,
      exp: summary?.exp ?? 0,
      guest: a.username === null,
      createdAt: a.createdAt,
    };
    if (summary) card.summary = { ...summary };
    return card;
  }

  /**
   * Operator/seed API: creates the initial state of an account that has none (profile.create; `dev` = the developer
   * test profile) whatever the trust level. The session entry is left to the first load. Returns the revision, or
   * null when the profile already had a state (kept unchanged).
   */
  async createInitialState(playerId: PlayerId, opts: { dev: boolean }): Promise<number | null> {
    return this.withRecord(playerId, async (rec, a) => {
      if (rec.profile.gameState !== null) return null;
      const nowMs = this.d.clock.now();
      const ctx = serverRuleContext({ nowMs, tzOffsetMin: rec.tz?.offsetMin ?? 0, random: this.d.ruleRng, serverState: rec.serverState });
      const state = runRulesOp('profile.create', emptyGameState(), { dev: opts.dev }, ctx, SERVER_RULE_OPS).gameState;
      rec.fresh = true;
      return (await this.commitCanonical(rec, a, state, nowMs, null, [], { kind: 'start', op: 'profile.create', dev: opts.dev })).revision;
    });
  }

  /**
   * Operator API (admin tools: grants of gold / items, repairs; tests: preparing a player): replaces the canonical
   * game state of a player that has one by `update(current)` - a function of the stored document (`{"$pi"}`
   * ProtectedInt format) that returns the new document or mutates it in place. The result must be readable by the
   * rules (decodeState) - otherwise VALIDATION_FAILED 'bad_state' and nothing is stored. The change is a new revision
   * like a command's (summary and team re-projected, `fresh` cleared, the server state kept) and every connection of
   * the player gets profile.changed, so running games reload it. With `baseRevision`, a state whose revision changed
   * meanwhile is not replaced (CONFLICT 'stale_revision'). `extra`: documents of the same change (its resource ledger
   * entry, services/resource-ledger.ts), built from the state before and after and the new revision, stored in ONE
   * atomic batch with the profile. Returns the new revision.
   */
  async adminSetState(
    playerId: PlayerId,
    update: (gameState: StoredGameState) => StoredGameState | void,
    opts: { baseRevision?: number; extra?: (change: AdminStateChange) => readonly StorageBatchOp[] } = {},
  ): Promise<number> {
    return this.withRecord(playerId, async (rec, a) => {
      if (rec.profile.gameState === null) throw new BackendError('INVALID_STATE', 'The player has no game state yet', { reason: 'no_profile' });
      // The console's state editor: the state was edited from this revision; a change in between is not overwritten.
      if (opts.baseRevision !== undefined && opts.baseRevision !== rec.profile.revision) {
        throw new BackendError('CONFLICT', 'The game state changed since it was loaded: reload it', { reason: 'stale_revision', currentRevision: rec.profile.revision });
      }
      const current = JSON.parse(JSON.stringify(rec.profile.gameState)) as StoredGameState;
      const next = update(current) ?? current;
      try {
        decodeState(next);
      } catch (err) {
        throw new BackendError('VALIDATION_FAILED', 'The new game state is not readable by the rules', { reason: 'bad_state', problem: (err as Error).message });
      }
      delete rec.fresh;
      const after = JSON.parse(JSON.stringify(next)) as StoredGameState;
      const nowMs = this.d.clock.now();
      const extra = opts.extra ? opts.extra({ before: rec.profile.gameState as unknown as StoredGameState, after, revision: rec.profile.revision + 1, at: nowMs }) : [];
      this.d.logger.info('game state replaced by an operator', { playerId });
      const ledgerOp = extra.find((o) => o.op === 'put' && o.collection === C.resourceLedger);
      return (await this.commitCanonical(rec, a, after, nowMs, null, extra, { kind: 'admin', ref: ledgerOp?.id ?? null })).revision;
    });
  }

  private buildTeam(a: AccountRecord, summary: ProfileSummary, input: TeamSnapshotInput): TeamSnapshot {
    return {
      playerId: a.playerId,
      displayName: a.displayName,
      level: input.level ?? summary.level,
      attack: input.attack ?? summary.attack,
      protect: input.protect ?? summary.protect,
      health: input.health ?? summary.health,
      worms: input.worms ?? summary.worms,
      spec: input.spec ?? summary.spec,
      perks: input.perks ?? {},
      weapons: input.weapons ?? {},
      wormNames: input.wormNames ?? [],
      implant: input.implant ?? 0,
      flag: input.flag ?? 0,
      takenAt: this.d.clock.now(),
    };
  }

  /** Team snapshot of a player (as stored with the last save or command), used for assault defenders. */
  async teamOf(playerId: PlayerId, override?: TeamSnapshotInput): Promise<TeamSnapshot | null> {
    const a = await this.auth.getAccount(playerId);
    if (!a) return null;
    const rec = await this.getRecord(playerId);
    const summary = rec?.profile.summary ?? emptyProfileSummary();
    const base: TeamSnapshotInput = rec?.team ? { ...rec.team } : {};
    return this.buildTeam(a, summary, { ...base, ...(override ?? {}) });
  }

  /**
   * L2: an import may replace only a fresh profile, and only while the backend allows imports. Throws FORBIDDEN with
   * a machine reason otherwise.
   */
  private assertImportAllowed(rec: ProfileRecord): void {
    // The same refusal as the router's for profile.importLegacy while the 'legacyImport' feature is off (system.hello).
    if (!this.d.config.allowLegacyImport) throw new BackendError('FORBIDDEN', 'Importing a saved profile is disabled on this server', { reason: 'feature_disabled', feature: 'legacyImport' });
    if (!isFreshProfile(rec)) throw new BackendError('FORBIDDEN', 'Only a new profile can be replaced by an imported one', { reason: 'profile_not_fresh' });
  }

  async save(playerId: PlayerId, p: PayloadOf<'profile.save'>, ctx: ConnectionContext | null): Promise<ResultOf<'profile.save'>> {
    return this.withRecord(playerId, async (rec, a) => {
      const cur = rec.profile;
      const reason = p.reason ?? 'action';
      if (this.authoritative) {
        if (reason !== 'import') {
          throw new BackendError('FORBIDDEN', 'The server owns the game state: change it with player.command', { reason: 'use_commands' });
        }
        this.assertImportAllowed(rec);
      }
      if (p.baseRevision !== cur.revision) {
        throw new BackendError('CONFLICT', `Profile revision is ${cur.revision}, save was based on ${p.baseRevision}`, { reason: 'revision_changed', currentRevision: cur.revision });
      }
      const consumed = p.consumedGrants ?? [];
      if (consumed.length) await this.grants.assertConsumable(playerId, consumed);
      const summary = this.projector.summary(p.gameState, p.summary, cur.revision > 0 ? cur.summary : null);
      if (this.validator) {
        const violations = this.validator.validate({ previous: cur.revision > 0 ? cur.summary : null, next: summary, reason, revision: cur.revision });
        if (violations.length) throw new BackendError('VALIDATION_FAILED', 'Profile rejected by server validation', { reason: 'profile_rejected', violations });
      }
      const now = this.d.clock.now();
      const next: PlayerProfile = {
        ...cur,
        displayName: a.displayName,
        revision: cur.revision + 1,
        updatedAt: now,
        gameState: p.gameState,
        summary,
        legacyImport: reason === 'import' ? null : cur.legacyImport,
      };
      const team = this.buildTeam(a, summary, this.projector.team(p.gameState, p.team));
      // Keep the server-owned fields of the document; an imported state starts without the server state of the old one.
      const saved: ProfileRecord = { ...rec, profile: next, team };
      delete saved.fresh;
      if (this.authoritative) saved.serverState = {};
      // Income attribution (./economy.ts): the gap is checked against the state this save replaces.
      saved.profile = cur;
      const change = this.attribute(saved, p.gameState, next.revision, now, reason === 'import' ? { kind: 'import' } : { kind: 'client_save', op: reason });
      saved.profile = next;
      // The new state and the grants it consumed: one atomic write (a crash never credits a grant twice or loses it).
      await this.d.storage.batch([putOp(C.profiles, playerId, saved), ...this.grants.consumeOps(consumed)]);
      await this.grants.consumed(consumed);
      for (const l of this.listeners) l(saved, a);
      this.announceEconomy(change);
      this.d.hub.send([playerId], 'profile.changed', { revision: next.revision, updatedAt: now }, ctx ?? undefined);
      return { revision: next.revision, updatedAt: now, serverTime: now, summary };
    });
  }

  /**
   * Stores a validated legacy save for the game to decode (the ported read_storage owns the full field
   * mapping). The summary is updated from the legacy scalars so leaderboards reflect the import at once.
   * L2: only into a fresh profile while imports are allowed; the game state is cleared, so the next profile.load
   * hands the client the legacy save to decode (and to store with profile.save { reason: 'import' }).
   */
  async importLegacy(playerId: PlayerId, legacy: string, baseRevision: number, ctx: ConnectionContext | null): Promise<ResultOf<'profile.importLegacy'>> {
    const insp = inspectLegacyStorage(legacy);
    if (!insp.ok) throw new BackendError('VALIDATION_FAILED', insp.message, { reason: insp.reason });
    return this.withRecord(playerId, async (rec, a) => {
      if (this.authoritative) this.assertImportAllowed(rec);
      if (baseRevision !== rec.profile.revision) {
        throw new BackendError('CONFLICT', `Profile revision is ${rec.profile.revision}`, { reason: 'revision_changed', currentRevision: rec.profile.revision });
      }
      const sc = readLegacyScalars(insp.slots);
      const now = this.d.clock.now();
      const summary: ProfileSummary = {
        level: this.levelOf(sc.exp),
        exp: sc.exp,
        rating: sc.rating + sc.achievRatingBonus,
        assaults: sc.assaults,
        attack: sc.attack,
        protect: sc.protect,
        health: sc.health,
        worms: sc.worms,
        spec: sc.spec,
        careerMax: sc.careerMax,
        missionsMax: sc.missionsMax,
        survivalMax: sc.survivalMax,
        bossMax: sc.bossMax,
      };
      const legacyImport: LegacyImport = { format: insp.format, data: insp.normalized.slice(0, insp.format === 'v45' ? 4000 : 2000), importedAt: now };
      const next: PlayerProfile = { ...rec.profile, revision: rec.profile.revision + 1, updatedAt: now, summary, legacyImport };
      const saved: ProfileRecord = { ...rec, profile: next, team: this.buildTeam(a, summary, { wormNames: sc.wormNames }) };
      if (this.authoritative) {
        next.gameState = null;
        saved.serverState = {};
        delete saved.fresh;
      }
      // The legacy save becomes a state only when the client stores it (profile.save { reason: 'import' }).
      if (next.gameState === null) delete saved.econ;
      await this.d.storage.put(C.profiles, playerId, saved);
      for (const l of this.listeners) l(saved, a);
      this.d.hub.send([playerId], 'profile.changed', { revision: next.revision, updatedAt: now }, ctx ?? undefined);
      this.d.logger.info('legacy save imported', { playerId, format: insp.format });
      return { revision: next.revision, legacyImport, summary };
    });
  }

  /**
   * profile.setDisplayName: the name checked by the nickname rules and claimed in the one namespace of names
   * (AuthService.changeDisplayName: a nickname account may only change the case of its nickname). The account, the
   * profile document (its public name and team snapshot) and the name's record change in one batch.
   */
  async setDisplayName(playerId: PlayerId, raw: string): Promise<{ displayName: string }> {
    const displayName = await this.auth.changeDisplayName(playerId, raw, (name, nicknameAccount, writes) =>
      this.d.mutex.run(profileLockKey(playerId), async () => {
        const a = await this.auth.requireAccount(playerId);
        a.displayName = name;
        if (nicknameAccount) a.nickname = name;
        a.updatedAt = this.d.clock.now();
        const rec = await this.getRecord(playerId);
        if (!rec) {
          await this.d.storage.batch([putOp(C.accounts, playerId, a), ...writes]);
          return;
        }
        rec.profile.displayName = name;
        if (rec.team) rec.team.displayName = name;
        await this.storeRecord(rec, a, true, [putOp(C.accounts, playerId, a), ...writes]);
      }),
    );
    return { displayName };
  }

  /**
   * Operator rename (admin console, AuthService.renameByOperator: the sign-up rules and the one namespace of names):
   * the account, its name index records and the profile's copies of the name change in one batch. A nickname account
   * signs in with the new nickname from now on. Returns the new and the previous name.
   */
  async renameByOperator(playerId: PlayerId, raw: string): Promise<{ displayName: string; previous: string }> {
    let previous = '';
    const displayName = await this.auth.renameByOperator(playerId, raw, (name, nicknameAccount, writes, username) =>
      this.d.mutex.run(profileLockKey(playerId), async () => {
        const a = await this.auth.requireAccount(playerId);
        previous = a.displayName;
        a.displayName = name;
        if (nicknameAccount) {
          a.nickname = name;
          a.username = username;
        }
        a.updatedAt = this.d.clock.now();
        const rec = await this.getRecord(playerId);
        if (!rec) {
          await this.d.storage.batch([putOp(C.accounts, playerId, a), ...writes]);
          return;
        }
        rec.profile.displayName = name;
        if (rec.team) rec.team.displayName = name;
        await this.storeRecord(rec, a, true, [putOp(C.accounts, playerId, a), ...writes]);
      }),
    );
    return { displayName, previous };
  }

  async getPublic(playerId: PlayerId): Promise<PublicProfile | null> {
    const a = await this.auth.getAccount(playerId);
    if (!a) return null;
    const rec = await this.getRecord(playerId);
    await this.clans.ready();
    return {
      playerId,
      displayName: a.displayName,
      createdAt: a.createdAt,
      updatedAt: rec?.profile.updatedAt ?? a.createdAt,
      clanId: this.clans.clanIdOf(playerId),
      clan: this.clans.briefOf(playerId),
      badges: [...a.badges],
      summary: rec?.profile.summary ?? emptyProfileSummary(),
      team: rec?.team ?? null,
    };
  }
}
